import React, { useState, useEffect } from 'react';
import type { JackpotComponent } from '../../../types/builder';

interface JackpotRendererProps {
  component: JackpotComponent;
}

export const JackpotRenderer: React.FC<JackpotRendererProps> = ({ component }) => {
  const { settings } = component;
  const [currentAmount, setCurrentAmount] = useState(settings.amount);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAmount((prev) => prev + settings.incrementAmount);
    }, settings.incrementSpeed);

    return () => clearInterval(interval);
  }, [settings.incrementSpeed, settings.incrementAmount]);

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const style = settings.style || 'default';

  // Default Style
  if (style === 'default') {
    return (
      <div
        style={{
          backgroundColor: settings.backgroundColor,
          color: settings.textColor,
          padding: `${settings.padding}px`,
          textAlign: settings.alignment,
        }}
      >
        <div className="max-w-md mx-auto text-center py-4">
          {settings.title && (
            <h2 
              className="text-base font-bold mb-2 uppercase tracking-wide"
              style={{ color: settings.textColor }}
            >
              {settings.title}
            </h2>
          )}
          <div 
            className="text-4xl font-bold"
            style={{ color: settings.amountColor || '#FFD700' }}
          >
            {settings.currency} {formatAmount(currentAmount)}
          </div>
        </div>
      </div>
    );
  }

  // Compact Style
  if (style === 'compact') {
    return (
      <div
        style={{
          backgroundColor: settings.backgroundColor,
          padding: `${settings.padding}px`,
        }}
      >
        <div className="flex items-center justify-center gap-3 py-2">
          {settings.showIcon && settings.icon && (
            <img src={settings.icon} alt="prize" className="w-6 h-6 object-contain" />
          )}
          {!settings.icon && settings.showIcon && (
            <span className="text-2xl">🏆</span>
          )}
          <div>
            {settings.title && (
              <div className="text-xs font-semibold uppercase tracking-wide opacity-75" style={{ color: settings.textColor }}>
                {settings.title}
              </div>
            )}
            <div className="text-xl font-bold" style={{ color: settings.amountColor || '#FFD700' }}>
              {settings.currency} {formatAmount(currentAmount)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modern Style
  if (style === 'modern') {
    return (
      <div
        style={{
          backgroundColor: settings.backgroundColor,
          padding: `${settings.padding}px`,
        }}
      >
        <div className="max-w-xl mx-auto bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur-md rounded-2xl p-8 border border-purple-500/30 shadow-2xl shadow-purple-500/20">
          <div className="text-center">
            {settings.showIcon && (
              <div className="mb-4">
                {settings.icon ? (
                  <img src={settings.icon} alt="prize" className="w-16 h-16 mx-auto object-contain drop-shadow-lg" />
                ) : (
                  <div className="text-6xl">🏆</div>
                )}
              </div>
            )}
            {settings.title && (
              <h2 className="text-lg font-bold mb-3 uppercase tracking-widest" style={{ color: settings.textColor }}>
                {settings.title}
              </h2>
            )}
            <div 
              className="text-5xl font-black tracking-tight bg-gradient-to-r from-yellow-300 to-yellow-500 bg-clip-text text-transparent drop-shadow-2xl"
              style={{
                WebkitTextFillColor: 'transparent',
                backgroundImage: `linear-gradient(to right, ${settings.amountColor || '#FFD700'}, ${settings.amountColor || '#FFD700'}dd)`,
              }}
            >
              {settings.currency} {formatAmount(currentAmount)}
            </div>
            <div className="mt-2 text-xs uppercase tracking-widest opacity-50" style={{ color: settings.textColor }}>
              Current Jackpot
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Luxe Style
  if (style === 'luxe') {
    return (
      <div
        style={{
          backgroundColor: settings.backgroundColor,
          padding: `${settings.padding}px`,
        }}
      >
        <div className="max-w-2xl mx-auto relative">
          {/* Decorative glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 via-yellow-300/30 to-yellow-400/20 blur-3xl"></div>
          
          {/* Main content */}
          <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 border-4 border-yellow-400/50 shadow-2xl">
            {/* Top decoration */}
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 flex gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center shadow-xl">
                {settings.showIcon && settings.icon ? (
                  <img src={settings.icon} alt="prize" className="w-7 h-7 object-contain" />
                ) : (
                  <span className="text-2xl">👑</span>
                )}
              </div>
            </div>

            <div className="text-center pt-4">
              {settings.title && (
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-yellow-400/50 to-transparent"></div>
                  <h2 
                    className="text-sm font-bold uppercase tracking-[0.3em] text-yellow-400"
                    style={{ color: settings.textColor }}
                  >
                    {settings.title}
                  </h2>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-yellow-400/50 to-transparent"></div>
                </div>
              )}
              
              <div 
                className="text-6xl font-black tracking-tight mb-2"
                style={{ 
                  color: settings.amountColor || '#FFD700',
                  textShadow: `0 0 30px ${settings.amountColor || '#FFD700'}80, 0 0 60px ${settings.amountColor || '#FFD700'}40`,
                }}
              >
                {settings.currency} {formatAmount(currentAmount)}
              </div>
              
              <div className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: settings.textColor, opacity: 0.6 }}>
                ★ Grand Prize ★
              </div>
            </div>

            {/* Bottom decoration */}
            <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-600 shadow-lg"></div>
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-600 shadow-lg"></div>
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-600 shadow-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback
  return null;
};

